<template>
  <div class="roster-calendar-container">
    <FullCalendar
      :defaultView="defaultView"
      :defaultDate="defaultDate"
      slot-duration="00:15:00"
      slot-label-interval="01:00"
      min-time="07:00"
      max-time="21:00"
      height="parent"
      :header="header"
      :plugins="plugins"
      :events="calendarEvents"
      :scroll-time="scrollTime"
      :now-indicator="true"
      :selectable="true"
      :droppable="true"
      :editable="true"
      @select="onSelect"
      @eventClick="({ event }) => onEventClick(event)"
      @eventDrop="({ event }) => onEventDrop(event)"
      @eventResize="({ event }) => onEventResize(event)"
      @datesRender="onDatesRender"
    />
    <b-modal id="event-modal" centered title="Schedule Event">
      <ValidationObserver v-slot="{ handleSubmit }">
        <b-form
          id="event-form"
          @submit.stop.prevent="handleSubmit(() => onSaveEvent(event))"
        >
          <ValidationProvider
            name="barber"
            rules="required"
            v-slot="{ dirty, validated, valid, errors }"
          >
            <b-form-group label="Barber">
              <b-form-select
                v-model="event.resourceId"
                :options="resourceOptions"
                :state="dirty || validated ? valid : null"
              />
              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </ValidationProvider>
          <b-form-group label="Event">
            <b-form-select v-model="event.type" :options="eventTypeOptions" />
          </b-form-group>
          <b-form-group label="From">
            <vue-ctk-date-time-picker
              format="YYYY-MM-DDTHH:mm:ssZ"
              formatted="dddd, DD MMMM YYYY hh:mm A"
              v-model="event.start"
              :minute-interval="15"
              :no-label="true"
              required
            />
          </b-form-group>
          <b-form-group label="To">
            <vue-ctk-date-time-picker
              format="YYYY-MM-DDTHH:mm:ssZ"
              formatted="dddd, DD MMMM YYYY hh:mm A"
              v-model="event.end"
              :minute-interval="15"
              :no-label="true"
              required
            />
          </b-form-group>
        </b-form>
      </ValidationObserver>
      <div slot="modal-footer" class="w-100 d-flex justify-content-between">
        <div>
          <b-button
            v-if="event._id"
            variant="outline-danger"
            @click.prevent="onDeleteEvent(event)"
          >
            <b-spinner
              v-if="isDeleting"
              variant="light"
              style="height: 20px; width: 20px;"
              label="Deleting event"
            ></b-spinner>
            <div v-else>
              Delete Event
            </div>
          </b-button>
        </div>
        <div>
          <b-button variant="link" class="mr-1" @click="hideEventModal">
            Cancel
          </b-button>
          <b-button variant="primary" type="submit" form="event-form">
            <b-spinner
              v-if="isSaving"
              variant="light"
              style="height: 20px; width: 20px;"
              label="Saving event"
            ></b-spinner>
            <div v-else>
              Save
            </div>
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

import { DEFAULT_ROSTER_EVENT, eventTypes } from '@/store/modules/roster';
import { forceViewValid, forceDateValid } from '@/calendars';

const today = moment();

export default {
  components: {
    FullCalendar,
    ValidationObserver,
    ValidationProvider
  },
  data() {
    let { view, date } = this.$route.params;

    view = forceViewValid(view);
    date = forceDateValid(date);

    return {
      header: {
        left: 'prev,next today',
        center: 'title',
        right: 'listDay,timeGridDay,timeGridWeek,dayGridMonth'
      },
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
      defaultView: view,
      defaultDate: date,
      scrollTime: today.format('HH') + ':00:00',
      event: _.cloneDeep(DEFAULT_ROSTER_EVENT),
      eventTypeOptions: [
        {
          value: eventTypes.RESOURCE_WORK_HOURS,
          text: 'Work Hours'
        },
        {
          value: eventTypes.RESOURCE_BREAK,
          text: 'Break'
        },
        {
          value: eventTypes.RESOURCE_HOLIDAY,
          text: 'Holiday'
        },
        {
          value: eventTypes.RESOURCE_SICK,
          text: 'Sick'
        }
      ],
      isSaving: false,
      isDeleting: false
    };
  },
  methods: {
    ...mapActions({
      fetchResources: 'resources/fetchResources',
      subscribeRoster: 'roster/subscribeRoster',
      unsubscribeRoster: 'roster/unsubscribeRoster',
      createEvent: 'roster/createEvent',
      updateEvent: 'roster/updateEvent',
      deleteEvent: 'roster/deleteEvent'
    }),
    isMidnight(date) {
      return moment(date).format('HH:mm') === '00:00';
    },
    isAllDay({ start, end }) {
      return this.isMidnight(start) && this.isMidnight(end);
    },
    sanitizeEvent(event) {
      let { start, end } = event;

      end =
        end ||
        moment(start)
          .add(1, 'day')
          .format();

      return {
        ...event,
        start: moment(start).format(),
        end: moment(end).format(),
        allDay: this.isAllDay(event)
      };
    },
    onSelect({ start, end }) {
      if (!end) end = moment(start).add(30, 'minutes');

      let event = {
        ...DEFAULT_ROSTER_EVENT,
        start,
        end
      };
      event = this.sanitizeEvent(event);

      this.setEvent(event);
      this.showEventModal();
    },
    onEventClick({ id }) {
      let event = this.roster[id];
      event = this.sanitizeEvent(event);

      this.setEvent(event);
      this.showEventModal();
    },
    onEventDrop(event) {
      this.onEventMove(event);
    },
    onEventResize(event) {
      this.onEventMove(event);
    },
    onEventMove({ id, start, end }) {
      let event = {
        ...this.roster[id],
        start,
        end
      };

      event = this.sanitizeEvent(event);

      this.onSaveEvent(event);
    },
    setEvent(event) {
      this.event = event;
    },
    async onSaveEvent(event) {
      this.showSavingIndicator();
      event = this.sanitizeEvent(event);

      try {
        if (event._id) {
          await this.updateEvent(event);
        } else {
          await this.createEvent(event);
        }
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: e.message
        });
      } finally {
        this.hideSavingIndicator();
        this.hideEventModal();
      }
    },
    async onDeleteEvent(event) {
      this.showDeletingIndicator();

      try {
        await this.deleteEvent(event);
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: e.message
        });
      } finally {
        this.hideDeletingIndicator();
        this.hideEventModal();
      }
    },
    showSavingIndicator() {
      this.isSaving = true;
    },
    hideSavingIndicator() {
      this.isSaving = false;
    },
    showDeletingIndicator() {
      this.isDeleting = true;
    },
    hideDeletingIndicator() {
      this.isDeleting = false;
    },
    showEventModal() {
      this.$bvModal.show('event-modal');
    },
    hideEventModal() {
      this.$bvModal.hide('event-modal');
    },
    getEventColor({ type }) {
      if (type === eventTypes.RESOURCE_WORK_HOURS) return 'blue';
      if (
        type === eventTypes.RESOURCE_BREAK ||
        type === eventTypes.RESOURCE_HOLIDAY
      )
        return 'orange';
      if (type === eventTypes.RESOURCE_SICK) return 'red';
      return 'grey';
    },
    getEventTitle({ resourceId, type }) {
      const resource = this.resources[resourceId];

      if (!resource) return '';

      let title = resource.name;

      if (type === eventTypes.RESOURCE_HOLIDAY) {
        title += ' (holiday)';
      } else if (type === eventTypes.RESOURCE_SICK) {
        title += ' (sick)';
      } else if (type === eventTypes.RESOURCE_BREAK) {
        title += ' (break)';
      }

      return title;
    },
    onDatesRender({ view }) {
      const { type, currentStart } = view;
      const date = moment(currentStart).format('YYYY-MM-DD');

      this.updateRouteParams({ view: type, date });
    },
    updateRouteParams({ view, date }) {
      const url = this.$route.path.replace(
        /roster.*/,
        `roster/${view}/${date}`
      );

      window.history.replaceState(null, '', url);
    }
  },
  computed: {
    ...mapGetters({
      roster: 'roster/roster',
      resources: 'resources/resources',
      resourcesList: 'resources/resourcesList'
    }),
    calendarEvents() {
      return Object.values(this.roster).map(event => {
        return {
          ...event,
          title: this.getEventTitle(event),
          backgroundColor: this.getEventColor(event),
          textColor: 'white',
          borderColor: 'transparent'
        };
      });
    },
    resourceOptions() {
      return [
        {
          value: null,
          text: 'Select a Barber',
          disabled: true
        },
        ...this.resourcesList.map(resource => ({
          value: resource._id,
          text: resource.name
        }))
      ];
    }
  },
  created() {
    try {
      this.subscribeRoster();
      this.fetchResources();
    } catch (e) {
      this.$notify({
        type: 'error',
        title: 'Error',
        message: e.message
      });
    }
  },
  beforeDestroy() {
    this.unsubscribeRoster();
  }
};
</script>
